import * as _ from 'lodash';
import { type VoidFunctionComponent } from 'react';

import { useParams, useQuery } from '../../../contexts/RouterContext';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../../generic/Pagination/consts';
import PaginationFooter from '../../generic/Pagination/PaginationFooter';
import type { SnippetsQueryParams } from '../../../../shared/snippets';
import type { RevisionRouteParams } from '../projectDashboardTypes';

import ConfidenceFilter from './ConfidenceFilter';
import SnippetsDrawer from './Drawer';
import { useSnippetsWithQueryParams } from './hooks';
import LicenseStatusFilter from './LicenseStatusFilter';
import SearchBar from './SearchBar';
import SnippetsTable from './Table';
import SnippetsTreeView from './TreeView';

import styles from './Snippets.module.less';

/**
 * SnippetsContainer - Route container component for the Snippet Scanning page within a project.
 */
const SnippetsContainer: VoidFunctionComponent = () => {
  //  Note: this will change (and probably be moved) but for now this pulls the dummy data so we can see it.
  const params = useParams<RevisionRouteParams>();
  const userRevisionLocator = `${params.project_id}$${params.revision_id}`;

  const [queryParams, setQuery] = useQuery<SnippetsQueryParams>();
  //  Fetch the snippets. This hook already manages the query params itself, so we don't need to pass them
  const { data: snippets } = useSnippetsWithQueryParams(userRevisionLocator);

  return (
    <>
      <h1 className={styles.heading}>
        Snippets
      </h1>
      <div className={styles.subheading}>
        <div className={styles.filters}>
          <div className={styles.search}>
            <SearchBar
              initialSearch={queryParams.search ?? ''}
              onSearchChange={(value) => {
                if (value) {
                  setQuery({ ...queryParams, search: value });
                } else {
                  // If the search is cleared, we want to remove the search from the params.
                  setQuery(_.omit(queryParams, 'search'));
                }
              }}
            />
          </div>
          <div className={styles.confidence}>
            <ConfidenceFilter
              initialValues={queryParams.confidence ?? []}
              onFilterChange={(values) => {
                if (values.length > 0) {
                  setQuery({ ...queryParams, confidence: values });
                } else {
                  // If the confidence filter is cleared, we want to remove it from the params.
                  setQuery(_.omit(queryParams, 'confidence'));
                }
              }}
            />
          </div>
          <div className={styles.licenseStatus}>
            <LicenseStatusFilter
              initialValues={queryParams.licenseStatus ?? []}
              onFilterChange={(values) => {
                if (values.length > 0) {
                  setQuery({ ...queryParams, licenseStatus: values });
                } else {
                  // If the license status filter is cleared, we want to remove it from the params.
                  setQuery(_.omit(queryParams, 'licenseStatus'));
                }
              }}
            />
          </div>
        </div>
        <div>
          {/* TODO: Add compare button */}
          {/* TODO: Add actions dropdown menu */}
        </div>
      </div>
      <div className={styles.layout}>
        <SnippetsTreeView
          userRevisionLocator={userRevisionLocator}
          selectedPath={queryParams.path ?? '/'}
          setSelectedPath={(path) => setQuery({ ...queryParams, path })}
        />
        <SnippetsTable userRevisionLocator={userRevisionLocator} />
      </div>
      <PaginationFooter
        page={Number(queryParams.page) || DEFAULT_PAGE}
        count={Number(queryParams.pageSize) || DEFAULT_PAGE_SIZE}
        totalItems={snippets?.totalCount ?? 0}
        setPage={((page) => setQuery({ ...queryParams, page }))}
        setCount={((pageSize) => setQuery({ ...queryParams, pageSize }))}
      />
      {/* The SnippetsDrawer manages its own open/close state based on the query params. */}
      <SnippetsDrawer userRevisionLocator={userRevisionLocator} />
    </>
  );
};

export default SnippetsContainer;
